import { useContext } from 'react';
import { authServiceContext, authStateContext } from './AuthServiceProvider';

/**
 * A React hook that returns the current authService or an Error if the authService could not be
 * instantiated. This hook is unsafe in the sense that the value returned might not be an
 * AuthService and needs to be checked before usage.
 *
 * If the return value is null, it is still being determined (loading).
 *
 * This must be nested beneath an <AuthServiceProvider /> as this uses its configuration.
 */
export function useAuthServiceUnsafe() {
  return useContext(authServiceContext);
}

/**
 * A React hook that returns the current authService.
 *
 * If the authService is null, it is still being determined (loading).
 *
 * If the authService from context is an error, that error will be thrown here. Note that because
 * a hook cannot be put inside a try/catch block (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level),
 * this hook should be used under a React ErrorBoundary (https://reactjs.org/docs/error-boundaries.html).
 *
 * This must be nested beneath an <AuthServiceProvider /> as this uses its configuration.
 */
export function useAuthService() {
  const authService = useAuthServiceUnsafe();
  if (authService instanceof Error) {
    throw authService;
  }
  return authService;
}

/**
 * A React hook that returns the current authState.
 *
 * If the authState is null, it is still being determined (loading).
 *
 * This must be nested beneath an <AuthServiceProvider /> as this uses its configuration.
 */
export function useAuthState() {
  return useContext(authStateContext);
}
